/**
 * Analysis definitions — the platform-wide *structure* of an analysis
 * (`/v1/dict/analysis-definitions`, visit-engine).
 *
 * One LOINC code is one definition, shared by every organization, and it is
 * **read-only** for the frontend: definitions are created by backend
 * migrations, never through the UI. What an organization may set for itself
 * (tube colour, analyzer, reference numbers) lives in
 * `@/entities/organization-analysis-config`.
 */

/** How a parameter's result is entered and interpreted. */
export const EAnalysisResultType = {
  NUMBER: 'NUMBER',
  DECIMAL: 'DECIMAL',
  ENUM: 'ENUM',
  TEXT: 'TEXT',
} as const

export type TAnalysisResultType =
  (typeof EAnalysisResultType)[keyof typeof EAnalysisResultType]

/**
 * Only numeric parameters carry organization-level numbers; ENUM/TEXT ones are
 * interpreted from the definition itself, so sending ranges for them is
 * rejected with `NUMERIC_CONFIG_NOT_APPLICABLE`.
 */
export const isNumericResultType = (resultType: string): boolean =>
  resultType === EAnalysisResultType.NUMBER ||
  resultType === EAnalysisResultType.DECIMAL

/**
 * Serology parameters reported as a signal/cutoff ratio — ВИЧ, Гепатит B,
 * Гепатит C and Сифилис. Only these take an organization `cutoffValue`, which
 * is what the backend compares the result against to derive
 * POSITIVE/NEGATIVE; every other numeric parameter is judged by its reference
 * range instead and must not carry a cutoff.
 *
 * The unit is checked alongside the codes so a serology definition added by a
 * later migration is recognized without a frontend change.
 */
const CUTOFF_PARAMETER_CODES = new Set(['HIV_SCO', 'HBV', 'HCV', 'SYPH'])

const CUTOFF_UNIT = 'S/CO'

export const isCutoffParameter = (
  parameterCode: string,
  defaultUnit?: string | null,
): boolean =>
  CUTOFF_PARAMETER_CODES.has(parameterCode.trim().toUpperCase()) ||
  defaultUnit?.trim().toUpperCase() === CUTOFF_UNIT

export const EAnalysisResultFormType = {
  TABLE: 'TABLE',
  TEXT: 'TEXT',
} as const

export type TAnalysisResultFormType =
  (typeof EAnalysisResultFormType)[keyof typeof EAnalysisResultFormType]

/**
 * Clinical meaning attached to an ENUM value at definition level — the lab
 * never picks it by hand, the backend derives the result's interpretation from
 * the chosen `value_code`.
 */
export const EAnalysisInterpretation = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  DOUBTFUL: 'DOUBTFUL',
  NOT_INTERPRETED: 'NOT_INTERPRETED',
} as const

export type TAnalysisInterpretation =
  (typeof EAnalysisInterpretation)[keyof typeof EAnalysisInterpretation]

export interface IAnalysisDefinitionEnumValue {
  id: string
  value_code: string
  value_text_ru: string
  value_text_uz_lat: string
  value_text_uz_cyr: string
  value_text_en: string
  /** Pre-assigned clinical meaning, `null` when the value carries none. */
  interpretation: TAnalysisInterpretation | null
  sort_order: number
  active: boolean
}

export interface IAnalysisDefinitionParameter {
  id: string
  parameter_code: string
  name_ru: string
  name_uz_lat: string
  name_uz_cyr: string
  name_en: string
  result_type: TAnalysisResultType | string
  default_unit: string | null
  analyzer_parameter_code?: string | null
  required: boolean
  sort_order: number
  /** Empty for numeric/text parameters. */
  enum_values: IAnalysisDefinitionEnumValue[]
}

export interface IAnalysisDefinition {
  id: string
  analysis_code: string
  name_ru: string
  name_uz_lat: string
  name_uz_cyr: string
  name_en: string
  laboratory_id?: string
  laboratory_name?: string
  result_form_type: TAnalysisResultFormType | string
  active: boolean
  parameters: IAnalysisDefinitionParameter[]
}

/** Row of the definitions list — no parameters, that needs the detail call. */
export interface IAnalysisDefinitionListItem {
  id: string
  analysis_code: string
  name_ru: string
  laboratory_name?: string
  result_form_type: TAnalysisResultFormType | string
  active: boolean
}

export interface IAnalysisDefinitionParams {
  /** Matched against the LOINC code and the names. */
  search?: string
  laboratoryId?: string
  resultFormType?: string
  active?: boolean
  page: number
  size: number
}

/** List result after the envelope has been normalized (see the api module). */
export interface IAnalysisDefinitionList {
  items: IAnalysisDefinitionListItem[]
  totalSize: number
}
